import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBoxesStacked,
  faBuilding,
  faChevronDown,
  faHand,
  faLayerGroup,
  faTag,
  faTruckFast,
  faTriangleExclamation,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { GloveBrandAlternatives } from '../components/GloveBrandAlternatives'
import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import {
  getProductPrimaryImage,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import { listDashboardNewsPosts } from '../lib/news'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/sarung-tangan-matahari'
const PAGE_CATEGORY = 'Sarung Tangan Matahari'
export const MATAHARI_META_TITLE_CORE = 'Distributor sarung tangan Matahari'
export const MATAHARI_META_DESCRIPTION =
  'Distributor sarung tangan Matahari untuk gudang, proyek, dan toko. Empat warna all size, MOQ 25 lusin, sample, stok, dan quotation via WhatsApp.'

const WHITE_IMAGE = toExternalImage(
  'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/d8c5e60934954b73aec12b461569e791.jpeg',
  { width: 900, format: 'webp', quality: 76 },
)
const NAVY_IMAGE = toExternalImage(
  'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/bb9c62e7c94c46a88ae394ea20f27943.jpeg',
  { width: 900, format: 'webp', quality: 76 },
)
const RED_IMAGE = toExternalImage(
  'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/9bcd4d137cfc4cda84de8c5825f4c2b8.jpeg',
  { width: 900, format: 'webp', quality: 76 },
)
const LIGHT_BLUE_IMAGE = toExternalImage(
  'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/81f1256a598942589eb942628295df1c.jpeg',
  { width: 900, format: 'webp', quality: 76 },
)

export const MATAHARI_PRODUCT_DEFINITIONS = [
  {
    name: 'Sarung Tangan Matahari Putih Dot Hitam All Size',
    slug: 'sarung-tangan-matahari-putih-dot-hitam-all-size',
    image: WHITE_IMAGE,
    label: 'Putih dot hitam',
    summary: 'Varian putih all size dengan bintik hitam pada sisi telapak dan jari.',
  },
  {
    name: 'Sarung Tangan Matahari Navy Dot All Size',
    slug: 'sarung-tangan-matahari-navy-dot-all-size',
    image: NAVY_IMAGE,
    label: 'Navy dot',
    summary: 'Varian navy all size dengan bintik pada sisi telapak dan jari.',
  },
  {
    name: 'Sarung Tangan Matahari Merah Bendera Dot Hitam',
    slug: 'sarung-tangan-matahari-merah-bendera-dot-hitam',
    image: RED_IMAGE,
    label: 'Merah bendera',
    summary: 'Varian merah bendera all size dengan bintik hitam pada sisi telapak dan jari.',
  },
  {
    name: 'Sarung Tangan Matahari Biru Muda Dot Hitam',
    slug: 'sarung-tangan-matahari-biru-muda-dot-hitam',
    image: LIGHT_BLUE_IMAGE,
    label: 'Biru muda',
    summary: 'Varian biru muda all size dengan bintik hitam pada sisi telapak dan jari.',
  },
] as const

type MatahariProduct = {
  name: string
  slug: string
  image: string
  label: string
  summary: string
}

const USE_CASES = [
  {
    icon: faWarehouse,
    title: 'Gudang & logistik',
    desc: 'Untuk memegang karton, kemasan, dan barang kering saat sortir, penyimpanan, atau pemindahan ringan.',
  },
  {
    icon: faBoxesStacked,
    title: 'Pengemasan barang',
    desc: 'Bintik PVC membantu cengkeraman saat pekerja melipat kardus, menata produk, dan menyiapkan pesanan.',
  },
  {
    icon: faBuilding,
    title: 'Kerja umum proyek',
    desc: 'Dapat dipakai untuk pekerjaan lapangan ringan dan pemindahan material kering yang tidak tajam.',
  },
  {
    icon: faHand,
    title: 'Pemakaian harian',
    desc: 'Pilihan ekonomis untuk pekerjaan rutin yang membutuhkan lapisan rajut dan grip tambahan pada telapak.',
  },
]

const LIMITATIONS = [
  'Benda tajam atau pekerjaan dengan risiko sayatan tinggi.',
  'Bahan kimia, cairan, minyak, atau permukaan yang sangat basah.',
  'Panas tinggi, pengelasan, dan pekerjaan kelistrikan.',
  'Pekerjaan dekat komponen mesin yang sedang berputar.',
]

const DROPIFY_REASONS = [
  {
    icon: faBolt,
    title: 'Cek stok keempat warna sekaligus',
    desc: 'Kirim pembagian warna dan jumlah lusin. Admin akan memeriksa stok per varian serta mengirim foto produk.',
  },
  {
    icon: faBoxesStacked,
    title: 'Campur warna dalam satu order',
    desc: 'Bisa selama stok setiap warna tersedia. Sebutkan pembagiannya, misalnya 10 lusin navy dan 15 lusin putih.',
  },
  {
    icon: faTag,
    title: 'Sample tersedia sebelum 25 lusin',
    desc: 'Sample dihitung harga satuan ditambah ongkir. Gunakan untuk mengecek rajutan, ukuran, warna, dan bintik sebelum membeli banyak.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim ke gudang, toko, atau proyek',
    desc: 'Pesanan dapat dikirim ke berbagai kota di Indonesia dengan ekspedisi yang disesuaikan dengan tujuan Anda.',
  },
]

const MATAHARI_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Apakah stok keempat warna Matahari bisa dicek sekaligus?',
    a: 'Bisa. Kirim pembagian warna dan jumlah lusin. Admin akan mengecek stok per varian serta mengirim foto aktual.',
  },
  {
    q: 'Toko saya hanya butuh satu warna dalam jumlah besar. Bisa?',
    a: 'Bisa. Sebutkan warnanya, misalnya navy saja, beserta total lusin. Admin mengecek stok varian itu dan mengirim foto aktual sebelum Anda membayar.',
  },
  {
    q: 'Bagaimana memastikan warna yang datang sesuai pesanan?',
    a: 'Minta foto stok aktual per warna sebelum transfer. Barang yang disiapkan mengikuti pembagian warna yang sudah dikonfirmasi di chat.',
  },
  {
    q: 'Apakah ukuran all size cocok untuk seluruh tim?',
    a: 'All size pada Matahari mengikuti standar umum sarung tangan rajut. Minta sample untuk mengecek kenyamanan pada anggota tim Anda.',
  },
  {
    q: 'Apa bedanya Matahari dengan sarung tangan bintik Diamond?',
    a: 'Matahari tersedia dalam empat pilihan warna dengan bintik hitam. Diamond menggunakan rajut abu-abu dengan bintik PVC kuning. Pilih sesuai kebutuhan warna tim atau stok toko.',
  },
]

export const MATAHARI_FAQ: FaqItem[] = [
  {
    q: 'Berapa minimum order sarung tangan Matahari?',
    a: 'Minimum ordernya mulai 25 lusin atau 300 pasang, dengan isi 12 pasang per lusin. Jika Anda ingin mencampur warna, kirim jumlah yang dibutuhkan untuk setiap warna agar admin dapat mengecek stoknya.',
  },
  {
    q: 'Apa perbedaan empat varian Matahari yang tersedia?',
    a: 'Ada empat pilihan warna: putih dengan bintik hitam, navy dengan bintik, merah bendera dengan bintik hitam, dan biru muda dengan bintik hitam. Semuanya berukuran all size dan dikemas 12 pasang per lusin. Pilih warna sesuai kebutuhan pembagian tim atau stok toko Anda.',
  },
  {
    q: 'Apakah warna boleh dicampur dalam satu pesanan?',
    a: 'Bisa selama stok setiap warna tersedia. Kirim pembagian yang Anda inginkan, misalnya 10 lusin navy dan 15 lusin putih. Admin akan mengecek jumlahnya dan mengirim foto stok aktual sebelum pesanan dikonfirmasi.',
  },
  {
    q: 'Apakah bisa memesan sample terlebih dahulu?',
    a: 'Bisa. Sample dihitung dengan harga satuan ditambah ongkir. Anda dapat mengecek rajutan, ukuran, warna, dan bintik pada telapak sebelum membeli untuk kebutuhan toko atau seluruh tim.',
  },
  {
    q: 'Apakah sarung tangan Matahari cocok untuk pekerjaan berminyak?',
    a: 'Sarung tangan Matahari lebih sesuai untuk memegang barang kering dan pekerjaan umum dengan risiko ringan. Untuk permukaan berminyak, benda tajam, bahan kimia, atau pekerjaan dengan standar K3 khusus, sampaikan kondisi kerjanya agar admin dapat membantu mencari seri yang lebih sesuai.',
  },
  {
    q: 'Bagaimana cara meminta harga dan ongkir?',
    a: 'Kirim pilihan warna, jumlah lusin, pembagian per warna, dan kota tujuan melalui WhatsApp. Admin akan mengecek stok, mengirim foto barang aktual, lalu memberikan harga, ongkir, dan pilihan pengiriman.',
  },
]

const MATAHARI_SCHEMA_PRODUCTS = MATAHARI_PRODUCT_DEFINITIONS.map((product, index) => ({
  '@type': 'ListItem',
  position: index + 1,
  item: {
    '@type': 'Thing',
    name: product.name,
    description: product.summary,
    image: product.image,
    url: absoluteUrl(`/produk/${product.slug}`),
    brand: { '@type': 'Brand', name: 'Matahari' },
  },
}))

function resolveMatahariProducts(products: StorefrontProduct[]): MatahariProduct[] {
  return MATAHARI_PRODUCT_DEFINITIONS.map((definition) => {
    const product = products.find(
      (item) =>
        item.slug === definition.slug ||
        item.name.trim().toLowerCase() === definition.name.toLowerCase(),
    )

    return {
      ...definition,
      slug: product?.slug || definition.slug,
      image: product
        ? getProductPrimaryImage(product, { width: 900, format: 'webp', quality: 76 })
        : definition.image,
    }
  })
}

export const Route = createFileRoute('/sarung-tangan-matahari')({
  loader: async () => {
    const [config, categories, productResponse, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ search: 'matahari', per_page: 20 }).catch(() => null),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      categories,
      products: resolveMatahariProducts(productResponse?.items ?? []),
      proofImages,
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle(MATAHARI_META_TITLE_CORE)
    const description = MATAHARI_META_DESCRIPTION

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: RED_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: RED_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'Matahari', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Merek', path: '/merek' },
              { name: 'Sarung Tangan Matahari', path: PAGE_PATH },
            ]),
          ),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema([...MATAHARI_FAQ, ...MATAHARI_TRUST_QUESTIONS])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Produk Sarung Tangan Matahari di Dropify',
            itemListElement: MATAHARI_SCHEMA_PRODUCTS,
          }),
        },
      ],
    }
  },
  component: MatahariRoute,
})

function MatahariRoute() {
  const { config, categories, products, proofImages, newsPosts } = Route.useLoaderData()
  return <MatahariLandingPage config={config} categories={categories} products={products} proofImages={proofImages} newsPosts={newsPosts} />
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  itemName,
  className,
}: {
  message: string
  label: string
  placement: string
  itemName?: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement,
          lp_category: PAGE_CATEGORY,
          ...(itemName
            ? {
                lead_item_name: itemName,
                items: [{ item_id: itemName, item_name: itemName, item_category: PAGE_CATEGORY }],
              }
            : {}),
        })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-xs font-black uppercase tracking-[0.22em] text-amber-700">
      {children}
    </p>
  )
}

export function MatahariLandingPage({
  config = null,
  categories = [],
  products = resolveMatahariProducts([]),
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  products?: MatahariProduct[]
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  const heroMessage =
    'Halo Dropify, saya mencari sarung tangan Matahari. Varian warna: ___. Kebutuhan: ___ lusin. Kota tujuan: ___. Bisa kirim stok per warna dan quotation?'

  return (
    <div className="min-h-screen bg-[#fffaf0] text-slate-950">
      <div className="bg-amber-300 px-4 py-2 text-center text-xs font-black text-[#292014] sm:text-sm">
        MOQ mulai 25 lusin (300 pasang) · 1 lusin isi 12 pasang · Sample tersedia
      </div>

      <header className="border-b border-white/10 bg-[#221f1a] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Periksa stok Matahari"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-amber-300/60 px-4 py-2.5 text-sm font-bold text-amber-200 transition-colors hover:bg-amber-300 hover:text-[#221f1a]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-amber-300">
          <div aria-hidden className="absolute -right-24 -top-32 h-[30rem] w-[30rem] rounded-full border-[80px] border-white/20" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="inline-flex rounded-full bg-[#221f1a] px-4 py-2 text-xs font-black uppercase tracking-wider text-amber-200">
                Sarung tangan rajut bintik PVC
              </p>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
                Distributor sarung tangan Matahari untuk <span className="underline decoration-white decoration-4 underline-offset-8">gudang, proyek, dan toko.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4b3a1d] md:text-lg">
                Rajutan berbintik PVC untuk menangani barang kering saat pengemasan, penataan stok, dan pekerjaan umum. Tersedia empat pilihan warna, minimum order mulai 25 lusin, dan dapat dikirim ke seluruh Indonesia.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton
                  message={heroMessage}
                  label="Minta quotation Matahari"
                  placement="hero_primary"
                  className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-amber-950/20 transition-colors hover:bg-green-800"
                />
                <a href="#produk-matahari" className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#221f1a]/30 px-6 py-4 text-sm font-black text-[#221f1a] transition-colors hover:bg-white/30">
                  Lihat empat variannya
                  <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {products.map((product, index) => (
                <article
                  key={product.slug}
                  data-hero-product-visual
                  className={`overflow-hidden rounded-[1.75rem] border-4 border-white bg-white shadow-xl ${index % 2 === 1 ? 'sm:translate-y-5' : ''}`}
                >
                  <a
                    href={`/produk/${product.slug}`}
                    aria-label={`Lihat detail ${product.name}`}
                    onClick={() => trackSelectItem({ item_id: product.slug, item_name: product.name, item_category: PAGE_CATEGORY }, 'Matahari hero products')}
                    className="block focus-visible:outline-4 focus-visible:outline-offset-[-4px] focus-visible:outline-amber-700"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      width="720"
                      height="540"
                      fetchPriority={index === 0 ? 'high' : undefined}
                      decoding="async"
                      className="aspect-[4/3] w-full object-contain"
                    />
                    <div className="border-t border-slate-100 px-5 py-4">
                      <span className="font-black">{product.label}</span>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-amber-100 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16">
            <div>
              <SectionLabel>Kenali produknya</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Apa itu sarung tangan Matahari?</h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="rounded-3xl bg-[#fff8df] p-6">
                <FontAwesomeIcon icon={faLayerGroup} className="h-6 w-6 text-amber-700" />
                <h3 className="mt-5 text-xl font-black">Rajutan untuk lapisan dasar</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">Bahan rajut menutup tangan saat memegang barang dan membantu mengurangi kontak langsung dengan debu serta gesekan ringan.</p>
              </div>
              <div className="rounded-3xl bg-[#221f1a] p-6 text-white">
                <FontAwesomeIcon icon={faHand} className="h-6 w-6 text-amber-300" />
                <h3 className="mt-5 text-xl font-black">Bintik PVC untuk grip</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">Bintik pada telapak dan jari membantu cengkeraman ketika pekerja memegang karton, kemasan, atau material kering.</p>
              </div>
              <p className="sm:col-span-2 text-base leading-relaxed text-slate-600">
                Matahari ditujukan untuk pekerjaan umum dengan risiko ringan. Pilih sarung tangan lain ketika pekerjaan melibatkan sayatan, bahan kimia, panas tinggi, kelistrikan, atau standar K3 khusus.
              </p>
            </div>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section id="produk-matahari" className="scroll-mt-6 bg-[#fffaf0] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <SectionLabel>Empat varian Matahari</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Cocokkan warna sebelum memesan</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Sebutkan warna saat meminta stok agar admin memeriksa produk yang tepat.</p>
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              {products.map((product) => (
                <article key={product.slug} className="group overflow-hidden rounded-[2rem] border border-amber-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                  <a
                    href={`/produk/${product.slug}`}
                    aria-label={`Lihat detail ${product.name}`}
                    onClick={() => trackSelectItem({ item_id: product.slug, item_name: product.name, item_category: PAGE_CATEGORY }, 'Matahari brand landing products')}
                    className="block overflow-hidden bg-white"
                  >
                    <img src={product.image} alt={product.name} width="900" height="675" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                  </a>
                  <div className="p-6 md:p-7">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-amber-700">{product.label}</p>
                    <h3 className="mt-2 text-2xl font-black tracking-tight">
                      <a
                        href={`/produk/${product.slug}`}
                        onClick={() => trackSelectItem({ item_id: product.slug, item_name: product.name, item_category: PAGE_CATEGORY }, 'Matahari brand landing products')}
                        className="transition-colors hover:text-amber-700"
                      >
                        {product.name}
                      </a>
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{product.summary}</p>
                    <WhatsAppButton
                      message={`Halo Dropify, saya mau cek ${product.name}. Kebutuhan: ___ lusin. Kota tujuan: ___. Bisa kirim foto stok dan harganya?`}
                      label="Cek stok varian ini"
                      placement="product_card"
                      itemName={product.name}
                      className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
                    />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
              <div className="rounded-[2rem] border border-slate-200 p-6 md:p-8">
                <SectionLabel>Cocok digunakan untuk</SectionLabel>
                <h2 className="mt-3 text-3xl font-black tracking-tight">Pekerjaan kering dengan risiko ringan</h2>
                <div className="mt-7 grid gap-4 sm:grid-cols-2">
                  {USE_CASES.map((item) => (
                    <article key={item.title} className="rounded-2xl bg-slate-50 p-5">
                      <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-amber-700" />
                      <h3 className="mt-4 font-black">{item.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                    </article>
                  ))}
                </div>
              </div>

              <aside className="rounded-[2rem] border border-red-200 bg-red-50 p-6 md:p-8">
                <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-red-700">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
                  Batas pemakaian
                </p>
                <h2 className="mt-3 text-3xl font-black tracking-tight text-red-950">Pilih seri lain untuk risiko berikut</h2>
                <p className="mt-4 text-sm leading-relaxed text-red-900">Sampaikan kondisi kerja kepada admin jika salah satu kondisi ini ada di area Anda.</p>
                <div className="mt-7 space-y-3">
                  {LIMITATIONS.map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl border border-red-200 bg-white/80 p-4 text-sm leading-relaxed text-red-950">
                      <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                      {item}
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <SectionLabel>Data pemesanan</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Warna, jumlah, quotation, dan pengiriman diperiksa sebelum order</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Kirim warna, jumlah lusin, dan kota tujuan. Admin akan memeriksa stok per warna, menyiapkan quotation, serta menginformasikan pilihan pengiriman.</p>
              <WhatsAppButton
                message={heroMessage}
                label="Chat admin Matahari"
                placement="why_dropify"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {DROPIFY_REASONS.map((item) => (
                <article key={item.title} className="rounded-2xl border border-amber-100 bg-[#fffaf0] p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#221f1a] text-amber-300">
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <SectionLabel>FAQ Matahari</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pertanyaan tentang sarung tangan Matahari</h2>
            </div>
            <div className="mt-9 divide-y divide-slate-200 border-y border-slate-200">
              {MATAHARI_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-slate-900">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-amber-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-amber-300 py-14 text-[#221f1a]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Siap cek stok</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim varian, jumlah lusin, dan kota tujuan Anda.</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#4b3a1d] md:text-base">Admin akan membalas dengan foto stok aktual, harga volume, dan pilihan pengiriman.</p>
            </div>
            <WhatsAppButton
              message={heroMessage}
              label="Minta quotation Matahari"
              placement="final_cta"
              className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#221f1a] px-7 py-4 text-base font-black text-white transition-colors hover:bg-slate-800"
            />
          </div>
        </section>

        <BrandTrustSections brandName="Matahari" itemName="sarung tangan Matahari" images={proofImages} questions={MATAHARI_TRUST_QUESTIONS} />
        <GloveBrandAlternatives currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
